/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AssetSourceKind } from "../commons/assetSource";
import { normalizeAssetSource } from "../commons/assetSource";
import { AssetCmDetails, CashUserList, CurrencyDetails, FixedIncomeList, IAssetIfDetails, IAssetStDetails, PropertyUserList, VariableIncomeList, VehiclesUserList } from "../responses/home";

export interface IAssetPrivateBondsInsert {
    assetGroupId: string;
    dueDate: Date;
    purchaseDate: Date;
    negotiatingId: string;
    issuingId: string;
    subType?: string;
    type: string;
    cdiTaxRate?: number | null;
    cdiPosTaxRate?: number | null;
    ipcaPosTaxRate?: number | null;
    igpmPosTaxRate?: number | null;
    yearTaxRate?: number | null;
    brokerageValue?: number;
    othersValue?: number;
    investedValue?: number;
}

export interface IAssetDetailsResponse {
    asset_info: IAssetInfo
}

export interface IAssetListTypesGroupped {
    groupName: string;
    names: { name: string; id: string; }[];
}

export interface IAssetTypesResponse {
    asset_list: IAssetTypes[];
}

export interface IAssetTypes {
    asset_name: string;
    asset_desc: string;
    asset_group: string;
    asset_family: string;
    asset_show_in_filter: string;
    _id: string;
}

export type AssetSameType = FixedIncomeList | CashUserList | VariableIncomeList;

export interface IFixedIncomeModel {
    title: string;
    mainType: AssetMainType;
    asset: Asset;
}

export interface ICashflowModel {
    title: string;
    mainType: AssetMainType;
    asset: Asset;
}

export interface IVehicleModel {
    title: string;
    mainType: AssetMainType;
    asset: Asset;
}

export interface IPropertyModel {
    title: string;
    mainType: AssetMainType;
    asset: Asset;
}

export interface IVariableIncomeModel {
    title: string;
    mainType: AssetMainType;
    asset: Asset;
}

export enum AssetType {
    fixedIncome = 'fixedIncome',
    variableRent = 'variableRent',
    cashflow = 'cashflow',
    vehicles = 'vehicles',
    properties = 'properties'
}

export class Wallet {
    constructor(
        public readonly amount: number
    ) { }
}

export enum AssetMainType {
    SavingAccount = "sa",
    PrivateBound = "pb",
    CheckingAccount = "ca",
    Customized = "cs",
    Debenture = "db",
    TreasureBound = "tb",
    Currency = "cy",
    Vehicle = "vh",
    Property = "py",
    Cripto = "cm",
    InvestimentFunds = "if",
    Stock = "st",
}

export enum IndexingAgentType {
    cdi = "cdi",
    ipca = "ipca",
    igpm = "igpm",
    pre = "pre"
}

export class IndexingAgent {
    constructor(
        public readonly type: IndexingAgentType,
        public readonly name: string,
        public readonly tax: number
    ) { }
}

export interface IAsset {
    id: string;
    asset_name: string;
    asset_group: string;
    asset_desc: string;
    asset_family: string;
}

export interface IAssetInfo {
    id: string;
    asset_id: string;
    user_id: string;
    asset_type: string;
    asset_sub_type: string;
    asset_alias: string;
    asset_performs_consolidation: boolean;
    asset_due_date: string;
    asset_purchase_date: string;
    asset_fin_issue_id: string;
    asset_fin_negotiating_id: string;
    asset_current_value: number;
    asset_create_date: string;
    asset_status: number;
    asset_pb_details: IAssetPbDetails;
    asset_ca_details: {
        ca_rate_cdi: number;
        ca_monthly_costs: number;
    };
    asset_cs_details: {
        cs_oc_brokerage: number;
        cs_oc_others: number;
    };
    asset_db_details: IAssetDbDetails;
    asset_tb_details: IAssetTbDetails;
    asset_event_user: IAssetEventUser[];
    asset_profit_benchmark: IAssetProfitBenchmark[];
    asset_profit_evolution: IAssetProfitEvolution[];
    asset_update_date: string;
    asset_last_consolidate_date: string;
    asset_sa_profit_evolution: any[];
    asset: IAsset;
    asset_fin_issue: IAssetFinIssue;
    asset_fin_negotiating: IAssetFinNegotiating;
    asset_cy_details: CurrencyDetails;
    asset_cm_details: AssetCmDetails;
    asset_st_details: IAssetStDetails;
    asset_if_details: IAssetIfDetails;
    asset_event_asset: IAssetEventAsset[];
    asset_fi_icon_url: string;
    asset_icon_url: string;
    asset_source?: string | null;
}

export interface IAssetEventAsset {
    id: string;
    event_type: string;
    event_create_date: string;
    event_date: string;
    event_value: number;
    event_fee: number;
    payment_date: null;
}

export interface IAssetProfitEvolution {
    reference_date: string;
    asset_balance: number;
    reference_ratio: number;
    period_profit: number;
    previous_event_asset_balance: number;
}

export interface IAssetProfitBenchmark {
    index_name: string
    month_reference: string
    month_profit_value: any
    month_initial_value: any
    month_percent_value: number
    month_evolution_value: any
    event_date: string;
    event_value: number;
}

export interface IAssetEventUser {
    id: string;
    event_type: string;
    event_create_date: string;
    event_date: string;
    event_value: number;
    event_recurring_value: number;
    event_recurring: boolean;
    event_quantity: number;
    event_quantity_amount: number;
}

export interface IAsset {
    id: string;
    asset_name: string;
    asset_group: string;
    asset_desc: string;
    asset_family: string;
}

export interface ICashUserList {
    id: string;
    asset_id: string;
    user_id: string;
    asset_type: string;
    asset_sub_type: string;
    asset_alias: any;
    asset_performs_consolidation: boolean;
    asset_due_date: string;
    asset_purchase_date: string;
    asset_fin_issue_id: string;
    asset_fin_negotiating_id: string;
    asset_current_value: number;
    asset_create_date: string;
    asset_status: number;
    asset_pb_details: IAssetPbDetails;
    asset_ca_details: IAssetCaDetails;
    asset_db_details?: IAssetDbDetails;
    asset_tb_details?: IAssetTbDetails;
    asset_event_user: IAssetEventUser2[];
    asset_profit_evolution: any[];
    asset_update_date: any;
    asset_last_consolidate_date: string;
    asset_sa_profit_evolution: any[];
    asset: IAsset;
    asset_fin_issue: IAssetFinIssue
    asset_fin_negotiating: IAssetFinNegotiating
    asset_fi_icon_url: string;
    asset_icon_url: string;
}

export interface IAssetCaDetails {
    ca_rate_cdi: number;
    ca_monthly_costs: number;
}

export interface IAssetEventUser2 {
    id: string;
    event_type: string;
    event_create_date: string;
    event_date: string;
    event_value: number;
}

export interface IAsset2 {
    id: string;
    asset_name: string;
    asset_group: string;
    asset_desc: string;
    asset_family: string;
}

export interface IFixedIncomeList {
    id: string;
    asset_id: string;
    user_id: string;
    asset_type: string;
    asset_sub_type?: string;
    asset_alias?: string;
    asset_performs_consolidation: boolean;
    asset_due_date: string;
    asset_purchase_date: string;
    asset_fin_issue_id: string;
    asset_fin_negotiating_id: string;
    asset_current_value: number;
    asset_create_date: string;
    asset_status: number; // = 1 em atualização
    asset_pb_details?: IAssetPbDetails;
    asset_ca_details?: IAssetCaDetails;
    asset_db_details?: IAssetDbDetails;
    asset_tb_details?: IAssetTbDetails;
    asset_event_user: IAssetEventUser[];
    asset_profit_evolution: any[];
    asset_update_date?: string;
    asset_last_consolidate_date: string;
    asset_sa_profit_evolution: any[];
    asset: IAsset2;
    asset_fin_issue: IAssetFinIssue
    asset_fin_negotiating: IAssetFinNegotiating
    asset_fi_icon_url: string;
    asset_icon_url: string;
}

export interface IAssetFinNegotiating {
    id: string;
    inst_country_prefix: string;
    inst_name: string;
    inst_situation: string;
    inst_address: IInstAddress;
}

export interface IAssetFinIssue {
    id: string;
    inst_country_prefix: string;
    inst_name: string;
    inst_situation: string;
    inst_address: IInstAddress;
}

export interface IInstAddress {
    uf: string;
    city: string;
}

export interface IAssetPbDetails {
    pb_type: string;
    pb_rate_cdi?: number;
    pb_rate_pos_plus_cdi: number;
    pb_rate_pos_plus_ipca: number;
    pb_rate_pos_plus_igpm?: number;
    pb_rate_pre_annual: any;
    pb_oc_brokerage: any;
    pb_oc_others: any;
}

export interface IAssetCaDetails {
    ca_rate_cdi: number;
    ca_monthly_costs: number;
    ca_rate_pos_plus_cdi: number;
    ca_rate_pos_plus_ipca: number;
    ca_rate_pos_plus_igpm?: number;
}

export interface IAssetDbDetails {
    db_code: string;
    db_rate_anual: any;
    db_quantity_prch: number;
    db_pu_value_prch: number;
    db_pu_value_actual: number;
    db_pu_value_upd_time: string;
    db_oc_brokerage: any;
    db_oc_others: any;
    debenture: IDebenture;
}

export interface IAssetTbDetails {
    tb_id: string;
    tb_oc_brokerage: number;
    tb_oc_others: number;
    treasure: ITreasure;
    tb_quantity_prch: number;
    tb_pu_value_prch: number;
    tb_rate_pre_plus: number;
}

export interface ITreasure {
    id: string;
    TrsrBd: ITrsrBd;
    TrsrBdType: ITrsrBdType;
    SelicCode: number;
    PrcgLst: IPrcgLst[];
}

export interface IPrcgLst {
    date_open: Date;
    date_close: Date;
    anul_red_rate: number;
    untr_red_value: number;
    anul_invstmt_rate: number;
    untr_invstmt_val: number;
}

export interface ITrsrBd {
    cd: number;
    nm: string;
    featrs: string;
    mtrtyDt: string;
    minInvstmtAmt: number;
    untrInvstmtVal: number;
    invstmtStbl: string;
    semiAnulIntrstInd: boolean;
    rcvgIncm: string;
    anulInvstmtRate: number;
    anulRedRate: number;
    minRedQty: number;
    untrRedVal: number;
    minRedVal: number;
    isinCd: string;
    FinIndxs: IFinIndxs;
    wdwlDt: null;
    convDt: null;
    BusSegmt: null;
    amortQuotQty: number;
}

export interface IFinIndxs {
    cd: number;
    nm: string;
}

export interface ITrsrBdType {
    cd: number;
    nm: string;
    ctdyRate: null;
    grPr: null;
}

export interface IDebenture {
    id: string;
    deb_code: string;
    deb_company: string;
    deb_situation: string;
    deb_emit_date: string;
    deb_due_date: string;
    deb_profit_start_date: string;
    deb_emission_value: number;
    deb_index: string;
    deb_reference_day_price_index: string;
    deb_amort_each: string;
    deb_amort_unity: string;
    deb_amort_grace: any;
    deb_amort_type: string;
    deb_interest_rate: number;
    deb_interest_each: string;
    deb_interest_unity: string;
    deb_bank_agent: string;
    deb_lead_coord: string;
    pu_history: IPuHistory[];
    debenture_event: IDebentureEvent[];
}

export interface IPuHistory {
    pu_date: string;
    pu_vl_nom: number;
    pu_fee: number;
    pu_prize: number;
    pu_unit_price: number;
    pu_calc_criteria: string;
    pu_situation: string;
}

export interface IDebentureEvent {
    event_date: string;
    event_type: string;
    event_fee?: number;
}


export interface IVehiclesUserList {
    id: string;
    user_id: string;
    vehicle_type: number;
    vehicle_type_desc: string;
    vehicle_fuel_type: number;
    vehicle_fuel_desc: string;
    vehicle_brand_id: string;
    vehicle_model_id: string;
    vehicle_model: IVehicleModelResponse;
    vehicle_year: string;
    vehicle_purchase_date: string;
    vehicle_purchase_value: number;
    vehicle_alias: string;
    vehicle_sync_status: number;
    vehicle_status: boolean;
    vehicle_last_consolidate_date: string;
    vehicle_valuation_evolution: any[];
    vehicle_valuation_value: number;
}

export interface IVehicleModelResponse {
    id: string;
    vehicle_model_label: string;
    vehicle_model_value: number;
    years: any[];
    brand: IBrand;
}

export interface IBrand {
    vehicle_model_brand_label: string;
    vehicle_model_brand_value: string;
}

export interface IAssetGoalUser {
    id: string;
    user_id: string;
    asset_goal_due_date: string;
    asset_goal_value: number;
    asset_goal_create_date: string;
    asset_goal_update_date: any;
}

export interface IPortfolioProfitEvolutionPeriod {
    actual_day: number;
    actual_month: number;
    last_12_month: number;
    last_24_month: number;
    from_start: number;
}

export interface IPortfolioProfitBenckmark {
    index_name: string;
    month_reference: string;
    month_profit_value?: number;
    month_initial_value?: number;
    month_percent_value: number;
}

export class AssetPersonalizedModel implements IFixedIncomeModel, IVariableIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.Customized;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly type: AssetType,
        public readonly amount: number,
        public readonly currentAmount: number,
        public readonly referenceDate: Date,
        public readonly taxReal: number,
        public readonly wallet: Wallet,
        public readonly returnValue: number|null,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Personalizado";
        this.asset = new Asset(id, name, amount, currentAmount, referenceDate, wallet, null, null, null, null, returnValue, finIssueName, sourceRaw);
    }
}

export class AssetCurrencyModel implements IVariableIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.Currency;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly type: AssetType,
        public readonly amount: number,
        public readonly currentAmount: number,
        public readonly referenceDate: Date,
        public readonly issuingBankName: string,
        public readonly negotiatingBankName: string,
        public readonly taxReal: number,
        public readonly wallet: Wallet,
        public readonly quantity: number|null,
        public readonly averagePrice: number|null,
        public readonly actualPrice: number|null,
        public readonly returnValue: number|null,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Moeda";
        this.asset = new Asset(id, name, amount, currentAmount, referenceDate, wallet, quantity, averagePrice, actualPrice, null, returnValue, finIssueName, sourceRaw);
    }
}

export class AssetFundModel implements IVariableIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.InvestimentFunds;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly type: AssetType,
        public readonly amount: number,
        public readonly currentAmount: number,
        public readonly referenceDate: Date,
        public readonly issuingBankName: string,
        public readonly negotiatingBankName: string,
        public readonly taxReal: number,
        public readonly wallet: Wallet,
        public readonly quantity: number|null,
        public readonly averagePrice: number|null,
        public readonly actualPrice: number|null,
        public readonly returnValue: number|null,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Fundos de Investimento";
        this.asset = new Asset(id, name, amount, currentAmount, referenceDate, wallet, quantity, averagePrice, actualPrice, null, returnValue, finIssueName, sourceRaw);
    }
}

export class AssetStockModel implements IVariableIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.Stock;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly type: AssetType,
        public readonly amount: number,
        public readonly currentAmount: number,
        public readonly referenceDate: Date,
        public readonly issuingBankName: string,
        public readonly negotiatingBankName: string,
        public readonly taxReal: number,
        public readonly wallet: Wallet,
        public readonly quantity: number|null,
        public readonly averagePrice: number|null,
        public readonly actualPrice: number|null,
        public readonly returnValue: number|null,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Ações";
        this.asset = new Asset(id, name, amount, currentAmount, referenceDate, wallet, quantity, averagePrice, actualPrice, null, returnValue, finIssueName, sourceRaw);
    }
}

export class AssetCriptoModel implements IVariableIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.Cripto;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly type: AssetType,
        public readonly amount: number,
        public readonly currentAmount: number,
        public readonly referenceDate: Date,
        public readonly issuingBankName: string,
        public readonly negotiatingBankName: string,
        public readonly taxReal: number,
        public readonly purchaseDate: Date,
        public readonly wallet: Wallet,
        public readonly quantity: number|null,
        public readonly averagePrice: number|null,
        public readonly actualPrice: number|null,
        public readonly returnValue: number|null,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Cripto Moeda";
        this.asset = new Asset(id, name, amount, currentAmount, purchaseDate, wallet, quantity, averagePrice, actualPrice, null, returnValue, finIssueName, sourceRaw);
    }
}

export class AssetCheckingAccountModel implements IFixedIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.CheckingAccount;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly type: AssetType,
        public readonly amount: number,
        public readonly currentAmount: number,
        public readonly creditDate: Date,
        public readonly issuingBankName: string,
        public readonly indexingAgent: IndexingAgent,
        public readonly purchaseDate: Date,
        public readonly wallet: Wallet,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Conta Corrente";
        this.asset = new Asset(id, name, amount, currentAmount, purchaseDate, wallet, null, null, null, null, null, finIssueName, sourceRaw);
    }
}

export class AssetTreasureBoundModel implements IFixedIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.TreasureBound;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly type: AssetType,
        public readonly unitPrice: number,
        public readonly investedValue: number,
        public readonly currentAmount: number,
        public readonly purchaseQty: number,
        public readonly purchaseDate: Date,
        public readonly dueDate: Date,
        public readonly issuingBankName: string,
        public readonly wallet: Wallet,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Tesouro Direto";
        this.asset = new Asset(id, name, investedValue, currentAmount, purchaseDate, wallet, null, null, null, dueDate, null, finIssueName, sourceRaw);
    }
}

export class AssetDebentureModel implements IFixedIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.Debenture;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly type: AssetType,
        public readonly purchaseValue: number,
        public readonly investedValue: number,
        public readonly currentAmount: number,
        public readonly puchaseQty: number,
        public readonly applyDate: Date,
        public readonly dueDate: Date,
        public readonly issuingBankName: string,
        public readonly purchaseDate: Date,
        public readonly wallet: Wallet,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Debênture";
        this.asset = new Asset(id, name, investedValue, currentAmount, purchaseDate, wallet, null, null, null, dueDate, null, finIssueName, sourceRaw);
    }
}

export class AssetSavingAccountModel implements IFixedIncomeModel {
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.SavingAccount;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly type: AssetType,
        public readonly investedValue: number,
        public readonly currentAmount: number,
        public readonly applyDate: Date,
        public readonly issuingBankName: string,
        public readonly purchaseDate: Date,
        public readonly wallet: Wallet,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Poupança";
        this.asset = new Asset(id, name, investedValue, currentAmount, purchaseDate, wallet, null, null, null, null, null, finIssueName, sourceRaw);
    }
}

export class AssetPrivateBondsModel implements IFixedIncomeModel {
    public readonly type: AssetType = AssetType.fixedIncome;
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.PrivateBound;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly investedValue: number,
        public readonly currentAmount: number,
        public readonly purchaseDate: Date,
        public readonly dueDate: Date,
        public readonly issuingBankName: string,
        public readonly negotiatingBankName: string,
        public readonly indexingAgent: IndexingAgent | null,
        public readonly wallet: Wallet,
        public readonly finIssueName: string | null,
        public readonly assetFiIconUrl: string | null,
        public readonly assetIconUrl: string | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Título Privado";
        this.asset = new Asset(id, name, investedValue, currentAmount, purchaseDate, wallet, null, null, null, dueDate, null, finIssueName, sourceRaw);
    }
}

export class AssetVehicleModel implements IFixedIncomeModel {
    public readonly type: AssetType = AssetType.vehicles;
    public readonly asset: Asset;
    public readonly title: string;
    public readonly mainType: AssetMainType = AssetMainType.Vehicle;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly purchaseValue: number,
        public readonly currentAmount: number,
        public readonly purchaseDate: Date,
        public readonly vehicleType: string,
        public readonly brand: string,
        public readonly vehicleModel: string,
        public readonly vehicleYear: Date,
        public readonly wallet: Wallet,
        sourceRaw?: string | null,
    ) {
        this.title = "Veículo";
        this.asset = new Asset(id, name, purchaseValue, currentAmount, purchaseDate, wallet, null, null, null, null, null, null, sourceRaw);
    }
}

export class AssetPropertyModel implements IFixedIncomeModel {
    public readonly type: AssetType = AssetType.properties;
    public readonly asset: Asset;
    public readonly title: string;

    public readonly mainType: AssetMainType = AssetMainType.Property;

    constructor(
        readonly id: string,
        readonly name: string,
        public readonly alias: string,
        public readonly purchaseValue: number,
        public readonly currentAmount: number,
        public readonly purchaseDate: Date,
        public readonly propertyType: string,
        public readonly wallet: Wallet,
        public readonly m2Value: number | null,
        public readonly area: number,
        public readonly valuationMin: number | null,
        public readonly valuationMax: number | null,
        public readonly valuationValue: number | null,
        sourceRaw?: string | null,
    ) {
        this.title = "Imóvel";
        this.asset = new Asset(id, name, purchaseValue, currentAmount, purchaseDate, wallet, null, null, null, null, null, null, sourceRaw);
    }
}

export class Asset {
    public readonly profit: number;
    public readonly profitPercentage: number;
    public readonly representWalletPercentage: number;
    public readonly source: AssetSourceKind;

    constructor(
        public readonly id: string,
        public readonly name: string,
        public readonly investedValue: number,
        public readonly currentAmount: number,
        public readonly purchaseDate: Date|null,
        public readonly wallet: Wallet,
        public readonly quantity: number|null,
        public readonly averagePrice: number|null,
        public readonly actualPrice: number|null,
        public readonly dueDate: Date|null,
        public readonly returnValue: number|null,
        public readonly finIssueName: string|null,
        sourceRaw?: string | null,
    ) {
        this.source = normalizeAssetSource(sourceRaw);
        this.profit = this.currentAmount - this.investedValue;
        this.profitPercentage = +((this.profit * 100) / this.investedValue).toFixed(2);
        this.representWalletPercentage = +((this.currentAmount * 100) / this.wallet.amount).toFixed(2);
        this.returnValue = this.returnValue ?? 0;
    }
}

export interface IAssetNameConstructor {
    asset_alias: string;
    asset_details: {
        currency_symbol: {
            name: string;
        };
        cripto_symbol: {
            currency_base: string;
        };
        if_ticker_symbol: string;
        st_ticker_symbol: string;
        db_code: string;
        pb_type: string;
        treasure: {
            TrsrBd: {
                nm: string;
            };
        };
    };
}

export class AssetFactory {
    public static getIndexer(asset: IAssetDetailsResponse|null|undefined) {
        if (!asset) {
            return null;
        }

        switch (asset.asset_info.asset_type) {
            case AssetMainType.PrivateBound:
                return asset.asset_info.asset_pb_details.pb_type;
            case AssetMainType.CheckingAccount:
                return null;
            case AssetMainType.Cripto:
                return null;
            case AssetMainType.Currency:
                return null;
            case AssetMainType.Customized:
                return null;
            case AssetMainType.Debenture:
                return null;
            case AssetMainType.InvestimentFunds:
                return null;
            case AssetMainType.Property:
                return null;
            case AssetMainType.SavingAccount:
                return null;
            case AssetMainType.Stock:
                return null;
            case AssetMainType.TreasureBound:
                return null;
            case AssetMainType.Vehicle:
                return null;
            default:
                return null;
        }
    }

    public static getTax(asset: IAssetDetailsResponse) {
        if (!asset) {
            return null;
        }

        switch (asset.asset_info.asset_type) {
            case AssetMainType.PrivateBound:
                return asset.asset_info.asset_pb_details.pb_rate_cdi 
                    ?? asset.asset_info.asset_pb_details.pb_rate_pos_plus_cdi 
                    ?? asset.asset_info.asset_pb_details.pb_rate_pos_plus_igpm 
                    ?? asset.asset_info.asset_pb_details.pb_rate_pos_plus_ipca 
                    ?? asset.asset_info.asset_pb_details.pb_rate_pre_annual;
            case AssetMainType.CheckingAccount:
                return null;
            case AssetMainType.Cripto:
                return null;
            case AssetMainType.Currency:
                return null;
            case AssetMainType.Customized:
                return null;
            case AssetMainType.Debenture:
                return null;
            case AssetMainType.InvestimentFunds:
                return null;
            case AssetMainType.Property:
                return null;
            case AssetMainType.SavingAccount:
                return null;
            case AssetMainType.Stock:
                return null;
            case AssetMainType.TreasureBound:
                return null;
            case AssetMainType.Vehicle:
                return null;
            default:
                return null;
        }
    }

    public static getNameByMainType(mainType: AssetMainType, asset: IAssetNameConstructor) {
        switch (mainType.toLowerCase()) {
            case AssetMainType.Customized:
                return asset.asset_alias ?? "Personalizado";
            case AssetMainType.Currency:
                return asset.asset_details.currency_symbol.name;
            case AssetMainType.Cripto:
                return asset.asset_details.cripto_symbol.currency_base;
            case AssetMainType.InvestimentFunds:
                return asset.asset_details?.if_ticker_symbol;
            case AssetMainType.Stock:
                return asset.asset_details?.st_ticker_symbol;
            case AssetMainType.CheckingAccount:
                return asset.asset_alias ?? "Conta Corrente";
            case AssetMainType.Debenture:
                return `Debênture - ${asset.asset_details?.db_code}`;
            case AssetMainType.PrivateBound:
                return asset.asset_details!.pb_type;
            case AssetMainType.SavingAccount:
                return "Poupança";
            case AssetMainType.TreasureBound:
                return asset.asset_details!.treasure.TrsrBd.nm!;
            case AssetMainType.Vehicle:
                return asset.asset_alias ?? "Veículo";
            case AssetMainType.Property:
                return asset.asset_alias ?? "Imóvel";
            default:
                return "";
        }
    }

    public static createFromVariable(variableIncome: VariableIncomeList, wallet: Wallet) {
        switch (variableIncome.asset_type.toLowerCase()) {
            case AssetMainType.Customized:
                return AssetFactory.buildPersonalized(variableIncome, wallet, AssetType.variableRent);
            case AssetMainType.Currency:
                return AssetFactory.buildCurrency(variableIncome, wallet, AssetType.variableRent);
            case AssetMainType.Cripto:
                return AssetFactory.buildCripto(variableIncome, wallet, AssetType.variableRent);
            case AssetMainType.InvestimentFunds:
                return AssetFactory.buildFund(variableIncome, wallet, AssetType.variableRent);
            case AssetMainType.Stock:
                return AssetFactory.buildStock(variableIncome, wallet, AssetType.variableRent);
            default:
                return null;
        }
    }

    public static createFromCashflow(cashflow: CashUserList, wallet: Wallet) {
        switch (cashflow.asset_type.toLowerCase()) {
            case AssetMainType.Customized:
                return AssetFactory.buildPersonalized(cashflow, wallet, AssetType.fixedIncome);
            case AssetMainType.CheckingAccount:
                return AssetFactory.buildCheckingAccount(cashflow, wallet, AssetType.fixedIncome);
            default:
                return null;
        }
    }

    public static createFromVehicle(vehicle: VehiclesUserList, wallet: Wallet) {
        return AssetFactory.buildVehicle(vehicle, wallet);
    }

    public static createFromProperty(property: PropertyUserList, wallet: Wallet) {
        return AssetFactory.buildProperty(property, wallet);
    }

    public static createFromFixed(fixedIncome: FixedIncomeList, wallet: Wallet) {
        switch (fixedIncome.asset_type.toLowerCase()) {
            case AssetMainType.Customized:
                return AssetFactory.buildPersonalized(
                    fixedIncome,
                    wallet,
                    AssetType.fixedIncome);
            case AssetMainType.CheckingAccount:
                return AssetFactory.buildCheckingAccount(
                    fixedIncome,
                    wallet,
                    AssetType.fixedIncome
                );
            case AssetMainType.Debenture:
                return AssetFactory.buildDebenture(
                    fixedIncome,
                    wallet,
                    AssetType.fixedIncome
                );
            case AssetMainType.PrivateBound:
                return AssetFactory.buildPrivateBonds(
                    fixedIncome,
                    wallet
                );
            case AssetMainType.SavingAccount:
                return AssetFactory.buildSavingAccount(
                    fixedIncome,
                    wallet,
                    AssetType.fixedIncome
                );
            case AssetMainType.TreasureBound:
                return AssetFactory.buildTreasureBound(
                    fixedIncome,
                    wallet,
                    AssetType.fixedIncome
                );
            default:
                return null;
        }
    }

    public static buildPersonalized(asset: AssetSameType, wallet: Wallet, assetType: AssetType) {
        return new AssetPersonalizedModel(
            asset.id,
            asset.asset_alias ?? "Personalizado",
            asset.asset_alias,
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            0,
            wallet,
            asset.asset_return_value,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildCurrency(asset: VariableIncomeList, wallet: Wallet, assetType: AssetType) {
        return new AssetCurrencyModel(
            asset.id,
            asset.asset_cy_details.currency_symbol.name,
            asset.asset_cy_details.currency_symbol.name,
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            asset.asset_fin_issue?.inst_name,
            asset.asset_fin_negotiating?.inst_name,
            0,
            wallet,
            asset.asset_cy_details?.cy_quantity_actual_amount ?? asset.asset_cy_details?.cy_quantity_prch,
            asset.asset_cy_details?.cy_avg_price,
            asset.asset_cy_details?.cy_current_price,
            asset.asset_return_value,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildStock(asset: VariableIncomeList, wallet: Wallet, assetType: AssetType) {
        return new AssetStockModel(
            asset.id,
            asset.asset_st_details?.st_ticker_symbol ?? "",
            asset.asset_st_details?.st_ticker_symbol ?? "",
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            asset.asset_fin_issue?.inst_name,
            asset.asset_fin_negotiating?.inst_name,
            0,
            wallet,
            asset.asset_st_details?.st_quantity_actual_amount ?? asset.asset_st_details?.st_qtd_prch ?? null,
            asset.asset_st_details?.st_avg_price ?? null,
            asset.asset_st_details?.st_current_price ?? null,
            asset.asset_return_value,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildFund(asset: VariableIncomeList, wallet: Wallet, assetType: AssetType) {
        return new AssetFundModel(
            asset.id,
            asset.asset_if_details?.if_ticker_symbol ?? asset.asset_if_details?.inv_fund?.denominacao_social ?? "",
            asset.asset_if_details?.if_ticker_symbol ?? asset.asset_if_details?.inv_fund?.denominacao_social ?? "",
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            asset.asset_fin_issue?.inst_name,
            asset.asset_fin_negotiating?.inst_name,
            0,
            wallet,
            asset.asset_if_details?.if_quantity_actual_amount ?? asset.asset_if_details?.if_qtd_prch ?? null,
            asset.asset_if_details?.if_avg_price ?? null,
            asset.asset_if_details?.if_current_price ?? null,
            asset.asset_return_value,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildCripto(asset: VariableIncomeList, wallet: Wallet, assetType: AssetType) {
        return new AssetCriptoModel(
            asset.id,
            asset.asset_cm_details.cripto_symbol.currency_base,
            asset.asset_cm_details.cripto_symbol.currency_base,
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            asset.asset_fin_issue?.inst_name,
            asset.asset_fin_negotiating?.inst_name,
            0,
            new Date(asset.asset_purchase_date),
            wallet,
            asset.asset_cm_details?.cm_quantity_actual_amount ?? asset.asset_cm_details.cm_quantity_prch,
            asset.asset_cm_details.cm_avg_price,
            asset.asset_cm_details.cm_current_price,
            asset.asset_return_value,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildPrivateBonds(asset: AssetSameType, wallet: Wallet) {
        return new AssetPrivateBondsModel(
            asset.id,
            asset.asset_pb_details!.pb_type,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            new Date(asset.asset_due_date ?? ""),
            asset.asset_fin_issue?.inst_name ?? "",
            asset.asset_fin_negotiating?.inst_name,
            this.getIndexingAgent(asset as FixedIncomeList),
            wallet,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildCheckingAccount(asset: AssetSameType, wallet: Wallet, assetType: AssetType) {
        return new AssetCheckingAccountModel(
            asset.id,
            asset.asset_alias ?? "Conta Corrente",
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            asset.asset_fin_issue?.inst_name ?? "",
            new IndexingAgent(
                IndexingAgentType.cdi,
                "CDI",
                asset.asset_ca_details?.ca_rate_cdi ?? 0
            ),
            new Date(asset.asset_purchase_date),
            wallet,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildDebenture(asset: AssetSameType, wallet: Wallet, assetType: AssetType) {
        return new AssetDebentureModel(
            asset.id,
            `Debênture - ${asset.asset_db_details?.db_code}`,
            assetType,
            asset.asset_db_details?.db_pu_value_prch ?? 0,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            asset.asset_db_details?.db_quantity_prch ?? 0,
            new Date(asset.asset_purchase_date),
            new Date(asset.asset_db_details!.debenture.deb_due_date),
            asset.asset_fin_issue?.inst_name ?? "",
            new Date(asset.asset_purchase_date),
            wallet,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildTreasureBound(asset: AssetSameType, wallet: Wallet, assetType: AssetType) {
        return new AssetTreasureBoundModel(
            asset.id,
            asset.asset_tb_details!.treasure.TrsrBd.nm!,
            assetType,
            asset.asset_tb_details?.tb_pu_value_prch ?? 0,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            asset.asset_tb_details?.tb_quantity_prch ?? 0,
            new Date(asset.asset_purchase_date),
            new Date(asset.asset_due_date ?? ""),
            asset.asset_fin_issue?.inst_name ?? "",
            wallet,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildSavingAccount(asset: AssetSameType, wallet: Wallet, assetType: AssetType) {
        return new AssetSavingAccountModel(
            asset.id,
            "Poupança",
            assetType,
            asset.asset_event_user.map(a => a.event_value).reduce((a, b) => a + b, 0),
            asset.asset_current_value,
            new Date(asset.asset_purchase_date),
            asset.asset_fin_issue?.inst_name ?? "",
            new Date(asset.asset_purchase_date),
            wallet,
            asset.asset_fin_issue?.inst_name ?? null,
            asset.asset_fi_icon_url ?? null,
            asset.asset_icon_url ?? null,
            asset.asset_source,
        );
    }

    public static buildVehicle(vehicle: VehiclesUserList, wallet: Wallet) {
        return new AssetVehicleModel(
            vehicle.id,
            vehicle.vehicle_model.vehicle_model_label ?? vehicle.vehicle_alias,
            vehicle.vehicle_alias,
            vehicle.vehicle_purchase_value,
            vehicle.vehicle_valuation_value,
            new Date(vehicle.vehicle_purchase_date),
            vehicle.vehicle_type_desc ?? "",
            vehicle.vehicle_model.brand.vehicle_model_brand_label,
            vehicle.vehicle_model.vehicle_model_label,
            new Date(vehicle.vehicle_year),
            wallet,
            vehicle.vehicle_source,
        );
    }

    public static buildProperty(property: PropertyUserList, wallet: Wallet) {
        return new AssetPropertyModel(
            property.id,
            property.property_alias ?? property.property_type_desc,
            property.property_alias,
            property.property_purchase_value,
            property.property_actual_value ?? property.property_purchase_value,
            new Date(property.property_purchase_date),
            property.property_type_desc,
            wallet,
            property.property_profit_evolution?.length
                ? property.property_profit_evolution[property.property_profit_evolution.length - 1].property_central_value
                : ((property.property_actual_value ?? property.property_purchase_value) / property.property_area),
            property.property_area,
            property.property_valuation_min,
            property.property_valuation_max,
            property.property_valuation_value,
            property.property_source,
        );
    }

    private static getIndexingAgent(fixedIncomeList: FixedIncomeList | undefined | null): IndexingAgent | null {
        if (!fixedIncomeList) {
            return null;
        }

        switch (fixedIncomeList.asset_sub_type?.toLowerCase()) {
            case "cdi":
                return new IndexingAgent(
                    IndexingAgentType.cdi,
                    fixedIncomeList.asset_sub_type,
                    fixedIncomeList.asset_pb_details?.pb_rate_cdi
                    ?? fixedIncomeList.asset_pb_details?.pb_rate_pos_plus_cdi
                    ?? 0
                );
            case "igpm":
                return new IndexingAgent(
                    IndexingAgentType.igpm,
                    fixedIncomeList.asset_sub_type,
                    fixedIncomeList.asset_pb_details?.pb_rate_pos_plus_igpm ?? 0
                );
            case "ipca":
                return new IndexingAgent(
                    IndexingAgentType.ipca,
                    fixedIncomeList.asset_sub_type,
                    fixedIncomeList.asset_pb_details?.pb_rate_pos_plus_ipca ?? 0
                );
            case "pre":
                return new IndexingAgent(
                    IndexingAgentType.pre,
                    fixedIncomeList.asset_sub_type,
                    fixedIncomeList.asset_pb_details?.pb_rate_pre_annual ?? 0
                )
            default:
                throw new Error("asset_sub_type is not mapped");
        }
    }
}